import React, { useMemo } from "react";
import { FormConfiguration } from "../shared/crud/form";
import { LegalInfo, LegalInfoPolicyType } from "./domain/legalInfo.entity";
import { translationFieldConfig } from "../shared/widgets/translation";
import { usePropertyLocales } from "../../components/property-locale-provider";
import SimpleCrud from "../shared/crud/simple-crud";
import { useApiClient } from "../../lib/http";
import { FormContainer } from "./form-style";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { SECTION } from "../../lib/constants";
import { HtmlEditorWidget } from "../shared/widgets/html";
import LegalInfoSchema from "./schema.json";
import { messages, legalInfoMessages } from "./messages";
import { useLegalInfoApiHandlers } from "./infrastructure/legalInfo.api";
import { FormattedMessage } from "react-intl";
import { PolicySelect } from "./PolicySelect";

function LegalInfoCrud({ propertyTicker }: { propertyTicker: string }) {
  const httpClient = useApiClient();
  const allowedLocales = usePropertyLocales();

  const formConfiguration: FormConfiguration<LegalInfo> = useMemo(
    () => ({
      id: {
        hidden: true,
        default: -1,
      },
      property: {
        messages: {
          label: messages.tableHeaders?.["property"],
        },
        widget: ({ value, onChange }) => (
          <PolicySelect value={value as LegalInfoPolicyType} onChange={onChange} />
        ),
      },
      hotelTicker: {
        hidden: true,
        default: propertyTicker,
      },
      translations: {
        ...translationFieldConfig(allowedLocales || []),
        messages: {
          label: legalInfoMessages.translations,
        },
        children: {
          id: {
            hidden: true,
          },
          text: {
            messages: {
              label: legalInfoMessages.text,
            },
            widget: HtmlEditorWidget,
          },
          ...translationFieldConfig(allowedLocales || []).children,
        },
      },
    }),
    [allowedLocales, propertyTicker]
  );

  const { onGetAll, onGetOne, onCreate, onUpdate, onDelete } = useLegalInfoApiHandlers(
    httpClient,
    propertyTicker
  );

  return (
    <SimpleCrud<LegalInfo>
      formConfiguration={formConfiguration}
      schema={LegalInfoSchema}
      tableColumns={{
        hotelTicker: {
          width: 220,
          renderer: ({ value }) => <span style={{ fontSize: "14px", color: "#222" }}>{value}</span>,
        },
        property: {
          renderer: ({ value }) => (
            <span style={{ fontSize: "14px", color: "#222" }}>
              <FormattedMessage
                {...(value === LegalInfoPolicyType.PRIVACY_POLICY
                  ? legalInfoMessages.privacyPolicy
                  : legalInfoMessages.communicationsPolicy)}
              />
            </span>
          ),
        },
      }}
      formContainer={FormContainer}
      messages={messages}
      onGetAll={onGetAll}
      onGetOne={onGetOne}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onDelete={onDelete}
      section={SECTION.LEGAL_INFO}
      hideExportButton={true}
    />
  );
}

export default withAuthorizationVerifier(LegalInfoCrud);
